const dayKey = (value) => {
  if (!value) return "";
  const text = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
};

const eachDay = (start, end) => {
  const days = [];
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime())) return days;
  while (cursor <= last) {
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    const date = String(cursor.getDate()).padStart(2, "0");
    days.push(`${cursor.getFullYear()}-${month}-${date}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

export const overlapDays = (from, to, rangeStart, rangeEnd) => {
  const start = dayKey(from) > dayKey(rangeStart) ? dayKey(from) : dayKey(rangeStart);
  const end = dayKey(to) < dayKey(rangeEnd) ? dayKey(to) : dayKey(rangeEnd);
  if (!start || !end || end < start) return 0;
  return eachDay(start, end).length;
};

const personKey = (row) => String(row.staffId || row.userId || row.name || "");

const rankPeople = (records, predicate, countKey) => {
  const map = new Map();
  records.forEach((row) => {
    if (!predicate(row)) return;
    const key = personKey(row);
    if (!key) return;
    const current = map.get(key) || {
      id: key,
      name: row.name || "N/A",
      department: row.department || "N/A",
      branch: row.branch || "N/A",
      count: 0,
      days: 0,
      onTimeDays: 0,
      lateDays: 0,
    };
    current[countKey] += 1;
    if (countKey !== "count") current.count = current[countKey];
    map.set(key, current);
  });
  return [...map.values()].sort(
    (a, b) => b[countKey] - a[countKey] || a.name.localeCompare(b.name)
  );
};

const leaveIdentity = (leave) => {
  const userId = String(
    leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id || leave.name || ""
  );
  return {
    userId,
    name: leave.user_info?.name || leave.name || "N/A",
    department: leave.department || leave.user_info?.department || "N/A",
    branch: leave.branch_info?.name || leave.branch || "N/A",
    from: leave.from || leave.leave_from_date,
    to: leave.to || leave.leave_to_date,
    leave_type: leave.leave_type || "",
  };
};

export const buildHrMonthStats = ({ records = [], leaves = [], monthStart, monthEnd }) => {
  const daily = eachDay(monthStart, monthEnd).map((date) => {
    const rows = records.filter((row) => dayKey(row.date) === date);
    return {
      date,
      label: date.slice(8),
      present: rows.filter((row) => row.isPresent).length,
      late: rows.filter((row) => row.isLate).length,
      absent: rows.filter((row) => row.isAbsent).length,
    };
  });

  const latePeople = rankPeople(records, (row) => row.isLate, "lateDays");
  const absentPeople = rankPeople(records, (row) => row.isAbsent, "count");
  const onTimeByPerson = new Map();

  records.forEach((row) => {
    if (!row.isPresent) return;
    const key = personKey(row);
    if (!key) return;
    const current = onTimeByPerson.get(key) || {
      id: key,
      name: row.name || "N/A",
      department: row.department || "N/A",
      branch: row.branch || "N/A",
      count: 0,
      onTimeDays: 0,
      lateDays: 0,
    };
    if (row.isLate) current.lateDays += 1;
    else current.onTimeDays += 1;
    current.count = current.onTimeDays;
    onTimeByPerson.set(key, current);
  });

  const punctualPeople = [...onTimeByPerson.values()]
    .filter((row) => row.onTimeDays > 0 && row.lateDays === 0)
    .sort((a, b) => b.onTimeDays - a.onTimeDays || a.name.localeCompare(b.name));

  const leaveMap = new Map();
  leaves.forEach((leave) => {
    const person = leaveIdentity(leave);
    const days = overlapDays(person.from, person.to, monthStart, monthEnd);
    if (!person.userId || days <= 0) return;
    const current = leaveMap.get(person.userId) || {
      id: person.userId,
      name: person.name,
      department: person.department,
      branch: person.branch,
      days: 0,
      count: 0,
      leave_type: person.leave_type,
    };
    current.days += days;
    current.count = current.days;
    if (person.leave_type && !String(current.leave_type).includes(person.leave_type)) {
      current.leave_type = current.leave_type
        ? `${current.leave_type}, ${person.leave_type}`
        : person.leave_type;
    }
    leaveMap.set(person.userId, current);
  });

  const leavePeople = [...leaveMap.values()].sort(
    (a, b) => b.days - a.days || a.name.localeCompare(b.name)
  );

  return {
    daily,
    lateMarks: records.filter((row) => row.isLate).length,
    absentMarks: records.filter((row) => row.isAbsent).length,
    onTimeMarks: records.filter((row) => row.isPresent && !row.isLate).length,
    presentMarks: records.filter((row) => row.isPresent).length,
    latePeople,
    absentPeople,
    punctualPeople,
    leavePeople,
    leaveDays: leavePeople.reduce((sum, row) => sum + row.days, 0),
    peopleLate: latePeople.length,
    alwaysOnTime: punctualPeople.length,
  };
};
